/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type DataTurboClient, type DataTurboShare, type FileSystem } from 'app/common/types/mo';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';

import { AddDataTurboUserComponent } from '../add-data-turbo-user/add-data-turbo-user.component';
import { DataTurboShareService } from '../data-turbo-share.service';
import { DataTurboShareStrService } from '../data-turbo-share-str.service';
import { DeleteDataTurboShareComponent } from '../delete-data-turbo-share/delete-data-turbo-share.component';

@Component({
    selector: 'app-front-table-data-turbo-user-share',
    templateUrl: './front-table-data-turbo-user-share.component.html',
    styleUrls: ['./front-table-data-turbo-user-share.component.scss'],
})
export class FrontTableDataTurboUserShareComponent extends TableAbstract<DataTurboClient> implements OnInit, OnChanges {
    @Input() tableData: DataTurboClient[] = [];
    @Input() dataTurboShare: DataTurboShare;
    @Input() selectFs: FileSystem;
    @Input() vstoreId: string;
    @Input() entry: FileSystemEntryE;
    @Input() isFromDetail = false;
    @Input() vstoreName: string;
    @Input() isCopyFrom = false;
    @Output() tableDataChange: EventEmitter<DataTurboClient[]> = new EventEmitter();
    @Output() clickAddBtn?: EventEmitter<any> = new EventEmitter();
    @Input() supportSmartGui = true;
    i18n = this.str.i18n;

    userData: DataTurboClient[];
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: DataTurboClient[] = [];
    tableRest: Rest;
    tableCountRest: Rest;

    addBtn: Button;
    removeBtn: Button;

    constructor(
        public str: DataTurboShareStrService,
        private buttonService: ButtonService,
        private service: DataTurboShareService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.initBtns();

        this.initTable();

        this.tableRest = this.isFromDetail ? this.service.getUserBatchRest(this.dataTurboShare.vstoreId, this.dataTurboShare.ID) : null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        const onlyCurrentValueResult = !changes.dataTurboShare?.previousValue && changes.dataTurboShare?.currentValue;
        if (onlyCurrentValueResult || (changes.dataTurboShare?.previousValue &&
            changes.dataTurboShare?.currentValue?.ID !== changes.dataTurboShare?.previousValue?.ID)) {
            this.updatePermissionTableInfo();
        }
    }

    updatePermissionTableInfo(): void {
        if (this.isFromDetail) {
            this.tableRest = this.service.getUserBatchRest(this.dataTurboShare.vstoreId, this.dataTurboShare.ID);
        } else {
            this.updateTableData();
        }
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-data-turbo-user-data-turbo-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            depPermission: ['task_W'],
            permission: ['share_W'],
            command: () => {
                this.add();
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-data-turbo-user-data-turbo-share',
            label: this.i18n.btn.removeBtn,
            permission: ['share_W'],
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }: { data?: DataTurboClient }) => {
                this.isFromDetail ? this.delete(this.selection) : this.remove(data ? [data] : this.selection);
            },
        });
    }


    initTable(): void {
        this.tableConfig = {
            dataKey: 'ID',
            selectionMode: 'checkbox',
            menuItems: [
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'userName',
                header: this.i18n.label.DataTurboUser,
                filterType: 'search',
                filterTrim: false,
            },
            {
                field: 'permission',
                header: this.i18n.label.permissionLevel,
                columnFilter: true,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, Number(value)),
            },
        ];
    }

    updateTableData(): void {
        this.tableDataChange.emit(this.tableData);
        this.dt.updateData(this.tableData);
    }

    add(): void {
        if (this.clickAddBtn) {
            this.clickAddBtn.emit();
        }

        // 没有租户信息无法添加权限
        if (!this.vstoreId && !this.selectFs) {
            return;
        }
        this.doAction(AddDataTurboUserComponent, {
            isFrontTable: true,
            isFromDetail: this.isFromDetail,
            supportSmartGui: this.supportSmartGui,
            vstoreId: this.vstoreId || this.selectFs.vstoreId,
            selectedVstore: {
                label: this.vstoreName || this.selectFs.vstoreName,
                value: this.vstoreId || this.selectFs.vstoreId,
            },
            DataTurboUsers: this.tableData,
            dataTurboShare: this.dataTurboShare,
            entry: this.entry,
            callBack: (datas: DataTurboClient[]) => {
                if (!this.isFromDetail) {
                    const newData = datas.filter((data) => {
                        const isExist = this.tableData.some((value) => value.userName === data.userName);
                        return !isExist;
                    });
                    newData.forEach((v: any) => {
                        v.permission = v.PERMISSION;
                    });
                    this.tableData = this.tableData.concat(...newData);
                }

                this.updatePermissionTableInfo();
            },
        });
    }

    remove(removeList: DataTurboClient[]): void {
        this.tableData = this.tableData.filter((data) => {
            const tempIndex = removeList.findIndex((removeItem) => removeItem.userName === data.userName);
            return tempIndex === -1;
        });

        this.updatePermissionTableInfo();
    }

    delete(dataList: DataTurboClient[]): void {
        this.doAction(DeleteDataTurboShareComponent, {
            dataList,
            isFromDetail: this.isFromDetail,
            callBack: () => {
                this.dt.refresh();
            },
        });
    }
}
